/** General game/world constants. Balancing: docs/01-game-design.md */
export const GAME = {
  /** Logic step (s) — 60 Hz fixed timestep */
  fixedStep: 1 / 60,
  /** Max time processed in a single frame (spiral-of-death protection) */
  maxFrameTime: 0.25,

  arena: {
    width: 2400,
    height: 1600,
    /** Thickness occupied by wall blobs (collision boundary stays inside) */
    wallThickness: 56,
    /** Distance between wall blobs */
    wallSpacing: 96,
  },

  /** Stick deflection that counts as one menu step (edge-triggered) */
  menuStickEdge: 0.5,

  camera: {
    /** Approach rate toward the target (per logic step) */
    lerp: 0.10,
    shakeDecay: 4.5,
  },

  /** Enemy damage per hit (docs/12 C1) — pattern damage is in "units" */
  enemyDamage: {
    bulletUnit: 8,
    bossContact: 18,
    minionTouch: 12,
    lava: 15,
  },

  run: {
    /**
     * Boss rotation (docs/01): seeded shuffle-bag — every eligible regular
     * boss is fought once before any repeats. Inside a cycle the draw leans
     * toward a def-tier that rises with depth (low tiers early, heavy later).
     */
    rotation: {
      /** Def-tier target = start + room * perRoom, capped at the top
       *  regular def-tier (4 — finale bosses sit outside the bag) */
      tierTargetStart: 1,
      tierTargetPerRoom: 0.17,
      tierTargetMax: 4,
      /** Draw weight = exp(-falloff * |def.tier - target|) */
      tierFalloff: 1.1,
      /** The last N fights never return right after a bag refill */
      noRepeatWindow: 2,
      /** Every Nth room is a FINALE fight (Warden / Avatar): rooms N-1, 2N-1, ... */
      finaleEvery: 7,
    },
    /**
     * Boss level from run DEPTH (docs/01): 1 + floor(room * perRoom) plus a
     * small bump per earlier encounter with the same boss (capped).
     */
    bossLevel: {
      perRoom: 0.85,
      perEncounter: 1,
      encounterCap: 3,
      /** Boss HP × (1 + hpPerLevel · (level - 1)) */
      hpPerLevel: 0.08,
    },
    /** Boss HP grows with the player's firepower: 1 + scale*(power-1) */
    bossHpPowerScale: 0.45,
    /** Upper bound for the power-based HP multiplier */
    bossHpPowerCap: 3.5,
    /** Firepower bonus per extra equipped weapon (×(1 + this·(n-1))) */
    bossHpPerExtraWeapon: 0.5,
    /** Surprise elite reinforcements at 50% HP from this boss level on (E5) */
    reinforceMinLevel: 9,
    reinforceHpPct: 0.5,
    /** Squad size: base + floor(tier / perTiers), capped */
    reinforceBase: 2,
    reinforcePerTiers: 2,
    reinforceMax: 4,

    /** Wave mode (docs/12 C8): minion waves soften you up BEFORE the boss */
    waves: {
      /** First room that runs waves (earlier rooms go straight to the boss) */
      minRoom: 2,
      /** Extra wave-ceiling step per this many rooms past minRoom */
      perRooms: 3,
      /** Minions in wave 1 / added per later wave */
      spawnBase: 4,
      spawnPerWave: 3,
      /** Waves from this index onward mix in elites */
      eliteFromWave: 1,
      /** Static bunkers join the waves from this room on */
      bunkerFromRoom: 3,
      /** The huge-shell dome appears from this room on (docs/12 M3) */
      domeFromRoom: 5,
      /** Hard cap on the RNG wave count (docs/12 M2) */
      maxWaves: 5,
    },

    /** Side squads that drop in DURING the boss fight (docs/12 M2) */
    bossAdds: {
      minRoom: 3,
      intervalMin: 18,
      intervalMax: 30,
      /** Skipped while this many enemies are already up */
      maxAlive: 6,
    },

    /**
     * Boss evolution by TIER (docs/02). Every `levelsPerTier` levels the boss
     * steps up a tier. Tier 0 keeps every phase's SIGNATURE attacks (slow);
     * higher tiers unlock the extra layers marked `unlockTier` plus the
     * boss's own `BossDef.evolve` layers, and keep ramping tempo/speed.
     */
    evolve: {
      /** Levels per tier step */
      levelsPerTier: 5,

      /** Fire tempo & bullet speed scale with tier (intensity 0..1+) */
      tempoBase: 0.55,
      tempoPerTier: 0.22,
      tempoCap: 1.6,
      /** Bullet speed: floor at tier 0, full by mid tiers */
      bulletSpeedFloor: 0.62,
      bulletSpeedPerTier: 0.14,
      bulletSpeedCap: 1.3,

      /** Movement speed by tier */
      moveFloor: 0.72,
      movePerTier: 0.12,
      moveCap: 1.6,

      /** Sidestep jink burst speed (px/s) by tier */
      jinkBase: 340,
      jinkPerTier: 40,
      jinkCap: 520,

      /** Damage stays soft for the first ~3 tiers (Lv1-15), then climbs */
      damageFloor: 0.55,
      damagePerTier: 0.16,
      damageCap: 1.7,

      /** Body tint per tier band (menacing as tiers climb) */
      tints: [0xffffff, 0xffe2d2, 0xffc4ab, 0xff9d8a, 0xe87a7a],
    },
  },
} as const;
